import React, { useCallback, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import DataGrid, {
    Column,
    Pager,
    Paging,
    Scrolling,
    SearchPanel,
    Summary,
    TotalItem,
} from 'devextreme-react/cjs/data-grid';
import { Button as DevXButton } from 'devextreme-react/button';
import CustomStore from 'devextreme/data/custom_store';
import * as XLSX from 'xlsx';
import moment from 'moment';
import notistack from 'modules/core/notistack';
import { getRewardReport } from '../services/reward.service';

const RewardReportList = () => {
    const [hasData, setHasData] = useState(false);

    const dataSource = useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const search = JSON.parse(JSON.stringify(loadOptions));
                    const parameterList = new URLSearchParams();
                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }

                    const response = await getRewardReport(parameterList);
                    setHasData(!!(response?.data && response.data.length > 0));
                    return response;
                } catch (error) {
                    console.error('Data loading error:', error);
                    return { data: [], totalCount: 0 };
                }
            },
        });
    }, []);

    const handleExportExcel = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            const response = await getRewardReport(params);

            if (!response?.data || response.data.length === 0) {
                notistack.error('No data available to export');
                return;
            }

            const exportData = response.data.map(({ id, createdOn, ...rest }: any) => rest);

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            worksheet['!cols'] = [
                { wch: 25 },
                { wch: 15 },
                { wch: 25 },
                { wch: 20 },
                { wch: 20 },
                { wch: 20 },
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Reward Report');
            XLSX.writeFile(workbook, `Reward_Report_${moment().format('YYYY-MM-DD')}.xlsx`);
        } catch (error: any) {
            console.error('Export error:', error);
            notistack.error(error.response?.data?.message || 'Failed to export reward report');
        }
    }, []);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="m-0">Reward Report</h5>
                        {hasData && (
                            <DevXButton
                                icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                                hint="Export to Excel"
                                onClick={handleExportExcel}
                            />
                        )}
                    </Card.Header>

                    <Card.Body className="pt-0">
                        <DataGrid
                            dataSource={dataSource}
                            showBorders
                            showRowLines
                            keyExpr="id"
                            remoteOperations
                            columnAutoWidth
                            onContentReady={(e) => {
                                const hasRows = e.component.totalCount() > 0;
                                e.component.option('pager.visible', hasRows);
                                e.component.option('paging.visible', hasRows);
                            }}
                        >
                            <Scrolling mode="standard" useNative scrollByContent scrollByThumb />
                            <Pager
                                allowedPageSizes={[10, 25, 50, 100]}
                                showPageSizeSelector
                                showNavigationButtons
                                displayMode="full"
                                showInfo
                            />
                            <Paging defaultPageSize={10} />
                            <SearchPanel visible width={240} placeholder="Search..." />

                            <Summary>
                                <TotalItem />
                            </Summary>

                            <Column
                                dataField="customerName"
                                caption="Customer Name"
                                dataType="string"
                                cssClass="text-center"
                            />
                            <Column
                                dataField="mobileNumber"
                                caption="Mobile Number"
                                dataType="number"
                                cssClass="text-center"
                            />
                            <Column
                                dataField="email"
                                caption="Email"
                                dataType="string"
                                cssClass="text-center"
                            />
                            <Column
                                dataField="balanceExpiredPoints"
                                caption="Balance Expired Points"
                                format="#,##0"
                                dataType="number"
                                cssClass="text-center"
                            />
                            <Column
                                dataField="expiringInCurrentMonth"
                                caption="Expiring in Current Month"
                                format="#,##0"
                                dataType="number"
                                cssClass="text-center"
                            />
                            <Column
                                dataField="expiringInCurrentQuarter"
                                caption="Expiring in Current Quarter"
                                format="#,##0"
                                dataType="number"
                                cssClass="text-center"
                            />
                        </DataGrid>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default RewardReportList;
